import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ScrollText } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["general", "security", "hr", "legal", "ai_governance"];

export default function Policies() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ category: "general", version: "1.0" });

  const load = () => api.get("/policies").then((r) => setItems(r.data.policies));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.body) return toast.error("Título y contenido");
    await api.post("/policies", form);
    toast.success("Política creada");
    setForm({ category: "general", version: "1.0" });
    setDlg(false);
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="policies-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">GOBIERNO CORPORATIVO</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Políticas</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Reglas que los agentes deben respetar. Categoría, versión y cumplimiento.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="pol-add">
              <Plus className="w-4 h-4 mr-2" /> Nueva política
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Nueva política</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Título</Label>
                <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pol-title" />
              </div>
              <div>
                <Label className="label-mono">Contenido</Label>
                <Textarea value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })}
                          className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[200px]" data-testid="pol-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Categoría</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pol-cat"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Versión</Label>
                  <Input value={form.version || ""} onChange={(e) => setForm({ ...form, version: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pol-ver" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="pol-submit">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="pol-empty">
          <ScrollText className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
          <div className="label-mono mb-2">SIN POLÍTICAS</div>
          <p className="text-sm text-[var(--text-dim)]">Empieza por: Política de uso de datos, IA responsable, seguridad, RGPD.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((p) => (
            <div key={p.id} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`pol-${p.id}`}>
              <div className="flex items-center justify-between mb-2">
                <Badge className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] font-mono text-[10px]">
                  {p.category}
                </Badge>
                <span className="font-mono text-[10px] text-[var(--text-dim)]">v{p.version}</span>
              </div>
              <div className="font-display font-bold text-base mb-2">{p.title}</div>
              <div className="text-xs text-[var(--text-dim)] leading-relaxed line-clamp-4 whitespace-pre-wrap">
                {p.body}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
